import '@quromedical/fhir-common';
import type {
  MedicationRouteCode,
  MedicationSchedule,
} from '@quromedical/models';

import {
  type AssertRule,
  type AssertRules,
  compose,
  resolveConstant,
  type SuccessResolvers,
} from '../composer';
import {
  allSchedules,
  medicationRouteCodes,
  normalSchedules,
} from './internal';

export const schedule = {
  allSchedules,
  normalSchedules,
};

type MedicationRuleName =
  | 'create-pharmacy-prescription'
  | 'create-patient-prescription'
  | 'create-usage'
  | 'unknown';

type MedicationParams = {
  use?: MedicationRuleName;
  canCreateUsage: boolean;
  canCreatePrescription: boolean;
};

type MedicationQuery = {
  routeCodes: MedicationRouteCode[];
  schedules: MedicationSchedule[];
};

const resolveEmptyQuery = resolveConstant<MedicationQuery>({
  routeCodes: [],
  schedules: [],
});

const assertCanCreateUsage: AssertRule<MedicationParams> = ({
  canCreateUsage,
}) => {
  if (canCreateUsage) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['Does not have permission to create medication usages'],
  };
};

const assertCanCreatePrescription: AssertRule<MedicationParams> = ({
  canCreatePrescription,
}) => {
  if (canCreatePrescription) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['Does not have permission to create prescription'],
  };
};

const medicationQueryAssertions: AssertRules<
  MedicationRuleName,
  MedicationParams
> = {
  'create-usage': [assertCanCreateUsage],
  'create-pharmacy-prescription': [assertCanCreatePrescription],
  'create-patient-prescription': [assertCanCreatePrescription],
};

const medicationQueryResolvers: SuccessResolvers<
  MedicationRuleName,
  MedicationQuery,
  MedicationParams
> = {
  'create-pharmacy-prescription': resolveConstant({
    schedules: allSchedules,
    routeCodes: medicationRouteCodes,
  }),
  'create-patient-prescription': resolveConstant({
    schedules: normalSchedules,
    routeCodes: medicationRouteCodes,
  }),
  'create-usage': resolveConstant({
    schedules: allSchedules,
    routeCodes: medicationRouteCodes,
  }),
};

const assertKnownUse: AssertRule<MedicationParams> = ({ use }) => {
  if (use !== 'unknown') {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['Medication use must be specified'],
  };
};

/**
 * Get query for medication based on the intended use
 */
export const resolveMedicationQuery = compose<
  MedicationQuery,
  MedicationParams,
  MedicationRuleName
>({
  getRuleName: ({ use = 'unknown' }) => use,
  assertions: medicationQueryAssertions,
  defaultAssertions: [assertKnownUse],
  resolvers: medicationQueryResolvers,
  defaultResolver: resolveEmptyQuery,
});
